import React, { FormEvent, useEffect, useState } from 'react';
import AdminErrorBanner from '../AdminErrorBanner';
import * as adminApi from '../../../../api/adminApi';
import modalStyles from './AdminModals.module.css';

interface CreateAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  token: string | null;
  newAdmin: { email: string; name: string; password: string; customRoleId?: string };
  onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onAdminCreate: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onSendCredentials: () => Promise<void>;
  adminCreationStatus: 'idle' | 'success' | 'error';
  adminCreationError: string | null;
  credentialsStatus: 'idle' | 'loading' | 'success' | 'error';
  credentialsError: string | null;
}

export const CreateAdminModal: React.FC<CreateAdminModalProps> = ({
  isOpen,
  onClose,
  token,
  newAdmin,
  onInputChange,
  onAdminCreate,
  onSendCredentials,
  adminCreationStatus,
  adminCreationError,
  credentialsStatus,
  credentialsError
}) => {
  const [roles, setRoles] = useState<adminApi.AdminCustomRole[]>([]);

  useEffect(() => {
    if (!isOpen || !token) return;
    adminApi.getAdminCustomRoles(token).then(setRoles).catch(() => setRoles([]));
  }, [isOpen, token]);

  if (!isOpen) return null;

  return (
    <div className={modalStyles.overlay} onClick={onClose}>
      <div className={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={modalStyles.header}>
          <h2 className={modalStyles.title}>Create New Admin</h2>
          <button onClick={onClose} className={modalStyles.closeBtn} aria-label="Close">✕</button>
        </div>

        <div className={modalStyles.body}>
          <form onSubmit={onAdminCreate}>
            {adminCreationError && (
              <AdminErrorBanner variant="inline" message={adminCreationError} />
            )}
            {credentialsError && (
              <AdminErrorBanner variant="inline" message={credentialsError} />
            )}
            <div className={modalStyles.formRow}>
              <label className={modalStyles.formCol}>
                <span className={modalStyles.formLabel}>Full Name</span>
                <input
                  name="name"
                  type="text"
                  placeholder="Jane Doe"
                  value={newAdmin.name}
                  onChange={onInputChange}
                  required
                  className={modalStyles.input}
                />
              </label>

              <label className={modalStyles.formCol}>
                <span className={modalStyles.formLabel}>Email</span>
                <input
                  name="email"
                  type="email"
                  placeholder="admin@example.com"
                  value={newAdmin.email}
                  onChange={onInputChange}
                  required
                  className={modalStyles.input}
                />
              </label>

              <label className={modalStyles.formCol}>
                <span className={modalStyles.formLabel}>Temporary Password</span>
                <input
                  name="password"
                  type="password"
                  placeholder="Strong temporary password"
                  value={newAdmin.password}
                  onChange={onInputChange}
                  required
                  className={modalStyles.input}
                />
              </label>

              <label className={modalStyles.formCol}>
                <span className={modalStyles.formLabel}>Assigned Role</span>
                <select
                  name="customRoleId"
                  value={newAdmin.customRoleId || ''}
                  onChange={onInputChange}
                  className={modalStyles.input}
                >
                  <option value="">Admin (default full access)</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </label>

              {adminCreationStatus === 'success' && (
                <div className={modalStyles.successAlert}>Admin created successfully!</div>
              )}
              {credentialsStatus === 'success' && (
                <div className={modalStyles.successAlert} role="status">
                  Credentials emailed to {newAdmin.email}. If it does not arrive within a few minutes, ask them to check the spam folder.
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={modalStyles.footer}>
              <div className={modalStyles.actions}>
                <button type="submit" className={`${modalStyles.btn} ${modalStyles.primary}`}>Create Admin</button>
                <button
                  type="button"
                  onClick={onSendCredentials}
                  disabled={credentialsStatus === 'loading'}
                  className={`${modalStyles.btn} ${modalStyles.neutral}`}
                >
                  {credentialsStatus === 'loading' ? 'Sending...' : 'Send Credentials'}
                </button>
                <button type="button" onClick={onClose} className={`${modalStyles.btn} ${modalStyles.neutral}`}>Cancel</button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
